/**
 * Application Constants
 *
 * These come from the server via the `#__config` data island, which derives them
 * from the ECS task env (`ARTWORK_LOWMID_ROOT` / `STATIC_ASSET_ROOT`, see
 * apps/pictufy/src/views/assets.ts). They were previously hardcoded here, which
 * meant a non-production environment silently got production URLs from roughly
 * half the codebase — the modals used these literals while Dashboard.tsx,
 * newsletter.ts and directoryUpload.ts read `config.*`.
 *
 * The literals survive only as the fallback for when the island is missing or a
 * value is empty (e.g. local dev with an unset env var).
 */
import { config } from "../config";

/**
 * Root URL for artwork images (server: `ARTWORK_LOWMID_ROOT`).
 *
 * ⚠️ Has NO production fallback, unlike `ASSET_ROOT` below, and the difference is
 * deliberate: artwork images are now served per environment
 * (`files.dev.pictufy.com` etc.), so falling back to `files.pictufy.com` would
 * make dev quietly render PRODUCTION's images and look completely healthy while
 * doing it.
 *
 * Empty makes every `src` relative, which 404s — a visibly broken grid. That is
 * the better failure: it is impossible to mistake for working. A deployed task
 * cannot reach it anyway, because env.ts refuses to boot in production without
 * ARTWORK_LOWMID_ROOT set; only local dev with an unset var lands here.
 */
export const IMG_ROOT = config.imgRoot || "";

/**
 * Root URL for static assets — FontAwesome, favicons, images (server: `STATIC_ASSET_ROOT`).
 *
 * This one KEEPS its fallback: the static bundle host is shared by every
 * environment on purpose (one content-hashed build is valid everywhere), so the
 * literal is correct rather than merely convenient.
 */
export const ASSET_ROOT = config.assetRoot || "https://static.pictufy.com/";

/**
 * Backend URL for mockup/interior rendering. Normally supplied by the server via
 * the `#__config` island; the fallback is the same-origin route the server mounts
 * (`/mockup-render`), used when the island is missing or empty.
 */
export const MOCKUP_BACKEND_URL = config.mockupBackendUrl || "/mockup-render";
